/**
 * Field Manager Hooks
 *
 * Purpose: Custom hooks for managing field data, dialogs, and state in FieldManager
 *
 * Hooks:
 * - useFieldData: Manages field list fetching, model loading, and access control
 * - useFieldDialogs: Manages modal and dialog states for create/edit/revert operations
 *
 * Author: Development Team
 * Created: 2025-01-21 (Refactoring Phase 2)
 */

import { useState, useCallback } from 'react';
import metadataService, {
  Field,
  FieldDefinition,
  Model,
  TenantInfo,
} from '../services/metadataService';

// ============================================================================
// useFieldData Hook
// ============================================================================

export interface FieldFilters {
  model_id: string;
  status: string;
  storage_type: string;
}

export interface UseFieldDataReturn {
  data: Field[];
  models: { group: string; options: Model[] }[];
  loading: boolean;
  error: string | null;
  parentFieldDefinition: FieldDefinition | null;
  effectiveCanEdit: boolean;
  canViewRelatedFields: boolean;
  loadData: () => Promise<void>;
  loadModels: () => Promise<void>;
  loadViewNames: (viewIds: string[]) => Promise<Record<string, string>>;
  checkParentFieldDefinitionAccess: (modelId: string) => Promise<{
    canEdit: boolean;
    canView: boolean;
    fieldDef: FieldDefinition | null;
  }>;
}

/**
 * Hook for managing field data, model loading, and access control
 */
export function useFieldData(
  tenantContext: TenantInfo,
  canEdit: boolean,
  filters: FieldFilters
): UseFieldDataReturn {
  const [data, setData] = useState<Field[]>([]);
  const [models, setModels] = useState<{ group: string; options: Model[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parentFieldDefinition, setParentFieldDefinition] = useState<FieldDefinition | null>(null);
  const [effectiveCanEdit, setEffectiveCanEdit] = useState(canEdit);
  const [canViewRelatedFields, setCanViewRelatedFields] = useState(true);

  // Load models for filter dropdown
  const loadModels = useCallback(async () => {
    try {
      // Get both normal and fielddef models
      const [normalModels, fielddefModels] = await Promise.all([
        metadataService.getNormalModels(),
        metadataService.getFieldDefinitionModels(),
      ]);

      // Create grouped options for the dropdown
      const groupedModels = [
        {
          group: 'Business Models',
          options: normalModels.sort((a, b) => a.name.localeCompare(b.name)),
        },
        {
          group: 'Field Definition Models',
          options: fielddefModels.sort((a, b) => a.name.localeCompare(b.name)),
        },
      ];

      setModels(groupedModels);
    } catch (err) {
      console.error('Failed to load models:', err);
    }
  }, []);

  // Load view names for popover
  const loadViewNames = useCallback(async (viewIds: string[]): Promise<Record<string, string>> => {
    try {
      const views = await metadataService.getViews();
      const viewNames: Record<string, string> = {};

      // Create a mapping of view_id to label
      views.forEach((view) => {
        if (viewIds.includes(view.view_id)) {
          viewNames[view.view_id] = view.label;
        }
      });

      return viewNames;
    } catch (err) {
      console.error('Failed to load view names:', err);
      return {};
    }
  }, []);

  // Check parent field definition access level for related fields
  const checkParentFieldDefinitionAccess = useCallback(
    async (
      modelId: string
    ): Promise<{
      canEdit: boolean;
      canView: boolean;
      fieldDef: FieldDefinition | null;
    }> => {
      if (!modelId || !modelId.startsWith('fd_')) {
        // Not a field definition model, use normal canEdit
        setEffectiveCanEdit(canEdit);
        setCanViewRelatedFields(true);
        setParentFieldDefinition(null);
        return { canEdit, canView: true, fieldDef: null };
      }

      try {
        // Get the field definition for this model
        const fieldDef = await metadataService.getFieldDefinition(modelId, 'GLOBAL');
        if (fieldDef) {
          setParentFieldDefinition(fieldDef);

          // Check access level for related fields
          if (tenantContext.is_global) {
            // GLOBAL users can always edit and view
            setEffectiveCanEdit(canEdit);
            setCanViewRelatedFields(true);
            return { canEdit, canView: true, fieldDef };
          } else {
            // Non-GLOBAL users: check access level
            const accessLevel = fieldDef.access;
            if (accessLevel === '0') {
              // Internal Only - not accessible at all
              setEffectiveCanEdit(false);
              setCanViewRelatedFields(false);
              return { canEdit: false, canView: false, fieldDef };
            } else if (accessLevel === '1') {
              // Client Read-Only - can view but not edit
              setEffectiveCanEdit(false);
              setCanViewRelatedFields(true);
              return { canEdit: false, canView: true, fieldDef };
            } else {
              // Client Overrideable (2) or Client Overrideable All (3) - can edit and view
              setEffectiveCanEdit(canEdit);
              setCanViewRelatedFields(true);
              return { canEdit, canView: true, fieldDef };
            }
          }
        } else {
          // Field definition not found, use normal canEdit
          setEffectiveCanEdit(canEdit);
          setCanViewRelatedFields(true);
          setParentFieldDefinition(null);
          return { canEdit, canView: true, fieldDef: null };
        }
      } catch (err) {
        console.error('Failed to check parent field definition access:', err);
        // Fallback to normal canEdit
        setEffectiveCanEdit(canEdit);
        setCanViewRelatedFields(true);
        setParentFieldDefinition(null);
        return { canEdit, canView: true, fieldDef: null };
      }
    },
    [tenantContext.is_global, canEdit]
  );

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let accessResult: { canEdit: boolean; canView: boolean; fieldDef: FieldDefinition | null } = {
        canEdit,
        canView: true,
        fieldDef: null,
      };

      // Check parent field definition access if we have a pre-selected model
      if (filters.model_id) {
        accessResult = await checkParentFieldDefinitionAccess(filters.model_id);
      } else {
        setEffectiveCanEdit(canEdit);
        setCanViewRelatedFields(true);
        setParentFieldDefinition(null);
      }

      // If user cannot view related fields (Internal Only), don't load data
      if (filters.model_id && !accessResult.canView) {
        setData([]);
        return;
      }

      // Call the actual API
      const response = await metadataService.getFields({
        tenant_id: tenantContext.tenant_id,
        model_id: filters.model_id || undefined,
        status: filters.status || undefined,
      });

      setData(response || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  }, [tenantContext.tenant_id, filters, checkParentFieldDefinitionAccess, canEdit]);

  return {
    data,
    models,
    loading,
    error,
    parentFieldDefinition,
    effectiveCanEdit,
    canViewRelatedFields,
    loadData,
    loadModels,
    loadViewNames,
    checkParentFieldDefinitionAccess,
  };
}

// ============================================================================
// useFieldDialogs Hook
// ============================================================================

export interface UseFieldDialogsReturn {
  // Create/Edit Modal
  modalOpen: boolean;
  modalMode: 'create' | 'edit';
  selectedField: Field | undefined;
  openCreateModal: () => void;
  openEditModal: (field: Field) => void;
  closeModal: () => void;

  // Revert Dialog
  revertDialogOpen: boolean;
  revertingField: Field | null;
  globalField: Field | null;
  revertLoading: boolean;
  openRevertDialog: (field: Field, globalFieldData: Field | null) => void;
  closeRevertDialog: () => void;
  setRevertLoading: (loading: boolean) => void;

  // Views Popover
  viewsPopoverAnchor: HTMLElement | null;
  viewsPopoverData: { viewIds: string[]; viewNames: Record<string, string> } | null;
  openViewsPopover: (
    anchor: HTMLElement,
    viewIds: string[],
    viewNames: Record<string, string>
  ) => void;
  closeViewsPopover: () => void;
}

/**
 * Hook for managing modal and dialog states
 */
export function useFieldDialogs(): UseFieldDialogsReturn {
  // Create/Edit Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedField, setSelectedField] = useState<Field | undefined>();

  // Revert Dialog state
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertingField, setRevertingField] = useState<Field | null>(null);
  const [globalField, setGlobalField] = useState<Field | null>(null);
  const [revertLoading, setRevertLoading] = useState(false);

  // Views Popover state
  const [viewsPopoverAnchor, setViewsPopoverAnchor] = useState<HTMLElement | null>(null);
  const [viewsPopoverData, setViewsPopoverData] = useState<{
    viewIds: string[];
    viewNames: Record<string, string>;
  } | null>(null);

  // Create/Edit Modal handlers
  const openCreateModal = useCallback(() => {
    setSelectedField(undefined);
    setModalMode('create');
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((field: Field) => {
    setSelectedField(field);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Revert Dialog handlers
  const openRevertDialog = useCallback((field: Field, globalFieldData: Field | null) => {
    setRevertingField(field);
    setGlobalField(globalFieldData);
    setRevertDialogOpen(true);
  }, []);

  const closeRevertDialog = useCallback(() => {
    setRevertDialogOpen(false);
    setRevertingField(null);
    setGlobalField(null);
    setRevertLoading(false);
  }, []);

  // Views Popover handlers
  const openViewsPopover = useCallback(
    (anchor: HTMLElement, viewIds: string[], viewNames: Record<string, string>) => {
      setViewsPopoverAnchor(anchor);
      setViewsPopoverData({ viewIds, viewNames });
    },
    []
  );

  const closeViewsPopover = useCallback(() => {
    setViewsPopoverAnchor(null);
    setViewsPopoverData(null);
  }, []);

  return {
    // Create/Edit Modal
    modalOpen,
    modalMode,
    selectedField,
    openCreateModal,
    openEditModal,
    closeModal,

    // Revert Dialog
    revertDialogOpen,
    revertingField,
    globalField,
    revertLoading,
    openRevertDialog,
    closeRevertDialog,
    setRevertLoading,

    // Views Popover
    viewsPopoverAnchor,
    viewsPopoverData,
    openViewsPopover,
    closeViewsPopover,
  };
}
